/**
 * API Superadmin Operations
 *
 * Handles superadmin-specific operations
 * @module portal/modules/api/superadmin
 */

import { apiUrl, resolveGlobalConfig, type ApiConfig } from './config.js';
import { ensureAccessToken } from './auth.js';

async function postWithJson(
  url: string,
  token: string,
  data: Record<string, unknown>
): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(data),
  });
}

/**
 * Fetch superadmin users list
 */
export async function fetchSuperadminUsers(cfg?: ApiConfig | null): Promise<unknown> {
  const resolvedCfg = cfg ?? resolveGlobalConfig();
  const token = await ensureAccessToken(resolvedCfg);
  if (!token) {
    throw new Error('Unable to fetch users: no access token');
  }

  const endpoint = apiUrl(resolvedCfg, 'superadmin/users');
  const response = await fetch(endpoint, {
    method: 'GET',
    headers: { Authorization: 'Bearer ' + String(token) },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unable to read error');
    throw new Error('Failed to fetch users: ' + response.status + ' ' + errorText);
  }

  return await response.json();
}

/**
 * Adjust user credits (superadmin)
 */
export async function adjustUserCredits(
  cfg?: ApiConfig | null,
  userId?: string,
  delta = 0,
  note?: string
): Promise<unknown> {
  if (!userId) {
    throw new Error('User id is required to adjust credits.');
  }
  const resolvedCfg = cfg ?? resolveGlobalConfig();
  const token = await ensureAccessToken(resolvedCfg);
  if (!token) {
    throw new Error('Authentication required');
  }

  const endpoint = apiUrl(resolvedCfg, `superadmin/users/${userId}/credits`);
  const response = await postWithJson(endpoint, token, { delta, note });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unable to read error');
    throw new Error('Failed to adjust credits: ' + response.status + ' ' + errorText);
  }

  return await response.json();
}

/**
 * Extend user subscription (superadmin)
 */
export async function extendUserSubscription(
  cfg?: ApiConfig | null,
  userId?: string,
  params: Record<string, unknown> = {}
): Promise<unknown> {
  if (!userId) {
    throw new Error('User id is required to extend subscription.');
  }
  const resolvedCfg = cfg ?? resolveGlobalConfig();
  const token = await ensureAccessToken(resolvedCfg);
  if (!token) {
    throw new Error('Authentication required');
  }

  const endpoint = apiUrl(resolvedCfg, `superadmin/users/${userId}/subscription/extend`);
  const response = await postWithJson(endpoint, token, params);

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unable to read error');
    throw new Error('Failed to extend subscription: ' + response.status + ' ' + errorText);
  }

  return await response.json();
}
